import { verifyDeck } from '@rummy/engine';
import type { TableView } from '@rummy/protocol';
import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/index.tsx';

/**
 * Commit–reveal deck verification (§5.1), run in the player's own browser
 * against the server's published commitment.
 *
 * The copy is deliberately careful. This proves the deck order was fixed before
 * play began and not reordered mid-hand. It does not prove the shuffle was
 * uniformly random, and it does not prevent a compromised server from choosing
 * a favourable order before committing. "Provably fair" is not supportable and
 * would be challenged, so it is not said.
 */
export function DeckVerification({ table }: { table: TableView }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'ok' | 'fail'>('idle');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setStatus('idle');
    setCopied(false);
  }, [table.handId]);

  const reveal = table.deckReveal;
  if (!reveal) return null;

  return (
    <div className="verify">
      <button type="button" className="btn btn--ghost btn--small" onClick={() => setOpen((v) => !v)}>
        {t('verify.heading')}
      </button>
      {open && (
        <div className="verify__panel">
          <p className="verify__explain">{t('verify.explain')}</p>
          <p className="verify__hash">
            <span>{t('verify.commit')}</span>
            <code>{table.deckCommit.slice(0, 32)}…</code>
          </p>
          <button
            type="button"
            className="btn btn--small"
            onClick={async () => {
              const ok = await verifyDeck(reveal.order, reveal.nonce, table.deckCommit);
              setStatus(ok ? 'ok' : 'fail');
            }}
          >
            {t('verify.check')}
          </button>
          <button
            type="button"
            className={`btn btn--ghost btn--small${copied ? ' btn--done' : ''}`}
            onClick={() => {
              const receipt = JSON.stringify({
                hand: table.handId,
                commit: table.deckCommit,
                nonce: reveal.nonce,
                order: reveal.order,
              });
              void navigator.clipboard?.writeText(receipt).then(() => setCopied(true)).catch(() => setCopied(false));
            }}
          >
            {copied ? t('verify.copied') : t('verify.copy')}
          </button>
          {status !== 'idle' && (
            <p className={status === 'ok' ? 'verify__ok' : 'verify__fail'} role="status">
              {status === 'ok' ? t('verify.ok') : t('verify.fail')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
